/* External */
import { useMemo, memo } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';

/* Ott-common */
import CommonSearchCard from '@dtvgo/search-card';
import Icon from '@dtvgo/icon';
import { startsAtOrEndsInText } from '@dtvgo/date-utils';

/* Assets */
import defaultSearchCard from '~/assets/theme/svg/default-search-card.svg';

/* Other */
import { getJsonImage } from 'src/theme/images';

/* Components */
import LiveStartTime from '../LiveStartTime';
import LiveInteractiveTag from '../LiveInteractiveTag';

/* Utils */
import { DOT_SEPARATOR, ImageRatio } from '../../utils/constants';
import {
  useTitle,
  isPlayingNow,
  getRating,
  joinArrayWithSeparator,
  joinArrayWithSpace,
  getReleaseYear,
  isSchedule,
  isInteractivityAllowed,
} from '../../utils/metaDataUtils';
import withInteractiveFocusable from '../../hocs/withInteractiveFocusable.hoc';
import { useFirstSubtitleSearchCard, getSecondSubtitleSearchCard, useThirdSubtitleSearchCard } from './utils';

/* Hooks */
import useChannel from '../../hooks/useChannel';
import useLiveContentTexts from '../../hooks/useLiveContentTexts';
import { useCurrentTime } from '../../providers/CurrentTimeProvider';
import useImageCard from '../../hooks/useImageCard';
import useLockIcon from '../../hooks/useLockIcon';
import parentalControlBlock from '../../utils/parentalControlUtils';
import { useUserAccount } from '../../providers/UserProvider';
import useContent from '../../hooks/useContent';

/* Styles */
import './SearchCard.scss';
import { Content, Schedule, Show } from '../../storage/models';

const IMG_WIDTH = 130;
const IMG_DIMENSION = ImageRatio.TWO_BY_THREE;

function SearchCard({
  className = '',
  result,
  focused,
  onMouseDown = undefined,
  onMouseEnter = undefined,
}) {
  const defaultImage = getJsonImage('default-search-card') || defaultSearchCard;
  const isScheduleFlag = isSchedule(result);
  const content = useContent(isScheduleFlag ? result.content : undefined) || result;
  const {
    sourceProvider,
    images,
  } = content || {};

  const channel = useChannel(
    isScheduleFlag ? result.channelId : undefined,
    sourceProvider,
    true,
  );

  const { uid: userId, iso2Code } = useUserAccount() || {};
  const isInteractive = isInteractivityAllowed(result, iso2Code, userId);

  const timeLiveContentTexts = useLiveContentTexts();

  const currentTime = useCurrentTime();

  const { timeZone } = useIntl();

  const isLivePlayingNow = isScheduleFlag && isPlayingNow(result, currentTime);

  const showLockIcon = useLockIcon(content);
  const isContentBlockedByParentalControl = parentalControlBlock(content);

  const startDateFormatted = useMemo(() => (
    isScheduleFlag && currentTime
    && startsAtOrEndsInText(result?.startTime, result?.endTime, timeLiveContentTexts, timeZone)
  ) || '', [isScheduleFlag, result?.startTime, result?.endTime, timeLiveContentTexts, currentTime, timeZone]);

  const { imageUrl } = useImageCard({
    images,
    imageSize: IMG_WIDTH,
    aspectRatio: IMG_DIMENSION,
  });

  const title = useTitle(content, result, true);
  const firstSubtitle = useFirstSubtitleSearchCard(content, isScheduleFlag, true);
  const secondSubtitle = useMemo(
    () => getSecondSubtitleSearchCard(content),
    [content],
  );
  const thirdSubtitle = useThirdSubtitleSearchCard(content);

  const contentInformation = useMemo(
    () => joinArrayWithSpace([
      getRating(content),
      joinArrayWithSeparator([
        getReleaseYear(content),
        secondSubtitle,
      ], DOT_SEPARATOR),
    ]),
    [content, secondSubtitle],
  );

  return (
    <CommonSearchCard
      className={`dtv-html5-c-search-card ${focused ? 'dtv-html5-c-search-card--focused' : ''
      } ${className}`}
      onMouseDown={onMouseDown}
      onMouseEnter={onMouseEnter}
    >
      <div className="dtv-html5-c-search-card__image">
        <CommonSearchCard.Image imageUrl={imageUrl || defaultImage} className="dtv-html5-c-search-card__image--radius" />
        {showLockIcon && <CommonSearchCard.Lock className="dtv-html5-c-search-card__lock" />}
        {(isContentBlockedByParentalControl && !showLockIcon) && (
          <div className="dtv-html5-c-search-card__parental-control-block">
            <Icon name="parental-block" className="dtv-html5-c-search-card__parental-control-icon" />
          </div>
        )}
      </div>

      <div className="dtv-html5-c-search-card__data">
        <div className="dtv-html5-c-search-card__header">
          { isLivePlayingNow
            ? (
              <div className="dtv-html5-c-search-card__third-subtitle">
                <span className="dtv-html5-c-search-card__third-subtitle__live-separator">{DOT_SEPARATOR}</span>
                <CommonSearchCard.ThridSubTitle className="dtv-html5-c-search-card__third-subtitle__live-message">
                  <FormattedMessage id="common.live" />
                </CommonSearchCard.ThridSubTitle>
              </div>
            )
            : (
              <div className={`dtv-html5-c-search-card__third-subtitle
                ${thirdSubtitle ? '' : 'dtv-html5-c-search-card__third-subtitle--empty'}`}
              >
                <CommonSearchCard.ThridSubTitle>
                  {thirdSubtitle}
                </CommonSearchCard.ThridSubTitle>
              </div>
            )}
          {isInteractive && (
            <div className="dtv-html5-c-search-card__interactivity-tag">
              <LiveInteractiveTag />
            </div>
          )}
          <CommonSearchCard.ChannelTitle className="dtv-html5-c-search-card__channel-title">
            {channel?.title}
          </CommonSearchCard.ChannelTitle>
        </div>
        <CommonSearchCard.Title className="dtv-html5-c-search-card__title">
          {title}
        </CommonSearchCard.Title>
        <CommonSearchCard.FirstSubTitle className="dtv-html5-c-search-card__first-subtitle">
          {firstSubtitle}
        </CommonSearchCard.FirstSubTitle>
        <CommonSearchCard.SecondSubTitle className="dtv-html5-c-search-card__second-subtitle">
          { contentInformation }
        </CommonSearchCard.SecondSubTitle>
        {isScheduleFlag && (
        <LiveStartTime
          startDate={result.startTime}
          endDate={result.endTime}
          className="dtv-html5-c-search-card__live-icon"
          showStartInText={false}
          showLiveIcon={false}
        >
          <CommonSearchCard.ThridSubTitle className="dtv-html5-c-search-card__day-and-start-date">
            {startDateFormatted}
          </CommonSearchCard.ThridSubTitle>
        </LiveStartTime>
        )}
      </div>
    </CommonSearchCard>
  );
}

SearchCard.propTypes = {
  className: PropTypes.string,
  result: PropTypes.oneOfType([
    PropTypes.instanceOf(Schedule),
    PropTypes.instanceOf(Content),
    PropTypes.instanceOf(Show),
  ]).isRequired,
  focused: PropTypes.bool.isRequired,
  onMouseDown: PropTypes.func,
  onMouseEnter: PropTypes.func,
};

export default memo(withInteractiveFocusable()(memo(SearchCard)));
